import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie, ChevronRight } from "lucide-react";
import {
  getConsent,
  setConsent,
  subscribeSettingsOpen,
} from "@/lib/consent";

/**
 * UK GDPR / PECR-aligned cookie consent banner.
 * - Renders nothing until we've read localStorage on the client (avoids
 *   hydration mismatch on the prerendered snapshot).
 * - Shows on first visit; can be re-opened later from the footer.
 * - Three actions: accept all / reject non-essential / customise.
 * - Customise panel toggles Analytics and Chat categories independently.
 * - Strictly-necessary functionality (site itself) is never blocked.
 */
const CookieConsent = () => {
  // "hydrated" gates all client-only rendering to avoid SSR/prerender mismatch
  const [hydrated, setHydrated] = useState(false);
  const [visible, setVisible] = useState(false);
  const [customising, setCustomising] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, chat: false });

  useEffect(() => {
    setHydrated(true);
    const existing = getConsent();
    if (!existing) {
      // No choice yet — show banner
      setVisible(true);
    } else {
      // Seed the customise-panel state with existing choice, in case user reopens
      setPrefs({ analytics: existing.analytics, chat: existing.chat });
    }
    // Allow the footer "Cookie settings" link to reopen this later
    return subscribeSettingsOpen(() => {
      const cur = getConsent();
      if (cur) setPrefs({ analytics: cur.analytics, chat: cur.chat });
      setCustomising(true);
      setVisible(true);
    });
  }, []);

  if (!hydrated || !visible) return null;

  const handleAcceptAll = () => {
    setConsent({ analytics: true, chat: true });
    setVisible(false);
    setCustomising(false);
  };

  const handleRejectNonEssential = () => {
    setConsent({ analytics: false, chat: false });
    setVisible(false);
    setCustomising(false);
  };

  const handleSaveCustom = () => {
    setConsent(prefs);
    setVisible(false);
    setCustomising(false);
  };

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[70] px-3 pb-3 sm:pb-4 sm:px-4 pointer-events-none"
      data-testid="cookie-consent"
    >
      <div
        className="max-w-5xl mx-auto rounded-2xl pointer-events-auto shadow-2xl"
        style={{
          background: "rgba(6, 11, 24, 0.96)",
          border: "1px solid var(--lt-border-strong)",
          backdropFilter: "blur(14px)",
        }}
      >
        {!customising ? (
          /* ---------- Main banner ---------- */
          <div className="p-5 sm:p-6 lg:p-7 grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8">
            <div className="lg:col-span-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[color:var(--lt-blue-soft)] inline-flex items-center gap-2">
                <Cookie size={13} /> Cookies on this site
              </p>
              <h2 className="mt-2 text-white font-display text-[19px] sm:text-[22px] leading-tight">
                We&apos;d like to use a few non-essential cookies.
              </h2>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[color:var(--lt-text-dim)]">
                We use strictly-necessary cookies to make this site work. With
                your permission we&apos;d also like to use{" "}
                <span className="text-white">Google Analytics 4</span> to
                understand which pages are useful, and{" "}
                <span className="text-white">Crisp Chat</span> so you can start
                a conversation with us. See our{" "}
                <Link
                  to="/cookie-policy"
                  className="text-[color:var(--lt-blue-bright)] hover:underline"
                  data-testid="cookie-consent-policy-link"
                >
                  Cookie Policy
                </Link>{" "}
                for detail — you can change your choice at any time from the
                footer.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-2 justify-center">
              <button
                type="button"
                onClick={handleAcceptAll}
                className="btn-primary rounded-md px-4 py-2.5 text-[13px] font-semibold inline-flex items-center justify-center"
                data-testid="cookie-consent-accept-all"
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={handleRejectNonEssential}
                className="btn-ghost rounded-md px-4 py-2.5 text-[13px] font-semibold inline-flex items-center justify-center"
                data-testid="cookie-consent-reject"
              >
                Reject non-essential
              </button>
              <button
                type="button"
                onClick={() => setCustomising(true)}
                className="text-[12.5px] text-[color:var(--lt-text-dim)] hover:text-white inline-flex items-center justify-center gap-1 mt-1"
                data-testid="cookie-consent-customise"
              >
                Customise <ChevronRight size={12} />
              </button>
            </div>
          </div>
        ) : (
          /* ---------- Customise panel ---------- */
          <div className="p-5 sm:p-6 lg:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[color:var(--lt-blue-soft)]">
                  Cookie preferences
                </p>
                <h2 className="mt-2 text-white font-display text-[19px] sm:text-[22px] leading-tight">
                  Choose which non-essential cookies to allow.
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setCustomising(false)}
                className="text-[color:var(--lt-text-mute)] hover:text-white p-1"
                aria-label="Back to summary"
                data-testid="cookie-consent-close-customise"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 space-y-3">
              {/* Strictly necessary — always on */}
              <div
                className="rounded-lg p-4"
                style={{
                  border: "1px solid var(--lt-border-strong)",
                  background: "rgba(255,255,255,0.02)",
                }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-white font-semibold text-[14px]">
                      Strictly necessary
                    </p>
                    <p className="mt-1 text-[13px] text-[color:var(--lt-text-dim)]">
                      Required for the site to function. Cannot be disabled.
                    </p>
                  </div>
                  <span
                    className="text-[11px] uppercase tracking-widest text-[color:var(--lt-text-mute)] mt-1"
                    data-testid="cookie-consent-necessary-locked"
                  >
                    Always on
                  </span>
                </div>
              </div>

              {/* Analytics toggle */}
              <ToggleRow
                testid="cookie-consent-toggle-analytics"
                title="Analytics cookies"
                description="Google Analytics 4 — helps us understand which pages are useful. Not used for advertising."
                on={prefs.analytics}
                onChange={() => toggle("analytics")}
              />

              {/* Chat toggle */}
              <ToggleRow
                testid="cookie-consent-toggle-chat"
                title="Chat & support cookies"
                description="Crisp Chat — powers the on-site chat widget so you can start a conversation with us."
                on={prefs.chat}
                onChange={() => toggle("chat")}
              />
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                onClick={handleRejectNonEssential}
                className="btn-ghost rounded-md px-4 py-2.5 text-[13px] font-semibold"
                data-testid="cookie-consent-customise-reject-all"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={handleSaveCustom}
                className="btn-primary rounded-md px-4 py-2.5 text-[13px] font-semibold"
                data-testid="cookie-consent-save"
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* --------- Small toggle row helper (dark-styled switch) --------- */
const ToggleRow = ({ title, description, on, onChange, testid }) => (
  <div
    className="rounded-lg p-4"
    style={{
      border: "1px solid var(--lt-border-strong)",
      background: "rgba(255,255,255,0.02)",
    }}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-white font-semibold text-[14px]">{title}</p>
        <p className="mt-1 text-[13px] text-[color:var(--lt-text-dim)]">
          {description}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={onChange}
        data-testid={testid}
        className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors mt-1"
        style={{
          background: on
            ? "var(--lt-blue-bright, #1f6bff)"
            : "rgba(255,255,255,0.15)",
        }}
      >
        <span
          className="inline-block h-4 w-4 rounded-full bg-white transition-transform"
          style={{ transform: on ? "translateX(24px)" : "translateX(4px)" }}
        />
      </button>
    </div>
  </div>
);

export default CookieConsent;
